import { useState, useEffect, useRef, useCallback } from 'react';
import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFilters } from '../contexts/FilterContext';
import { API } from '../utils/api';
import { extractHashtags } from '../utils/hashtags';
import { KNOWN_LOCATIONS, ZIP_REGIONS } from '../components/Map/MapConstants';
import useXHR from './useXHR';

const CATEGORY_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#22c55e',
  '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6',
  '#FFC107', '#FFD54F', '#a855f7', '#d946ef', '#FFD700', '#f43f5e'
];

const MAP_REFRESH_INTERVAL = 30000;

const useMapPageData = (showToast) => {
  const { token, user } = useAuth();
  const { get, post, setMounted } = useXHR(token);
  const filters = useFilters();
  const {
    educatedIndividualFilters, setEducatedIndividualFilters,
    eiMinWords, setEiMinWords,
    minWordCount, setMinWordCount,
    documentTypes, selectedDocTypes, setSelectedDocTypes,
    docTypeMinWords, setDocTypeMinWords,
    certificationFilters, setCertificationFilters,
    certMinWords, setCertMinWords,
    contentMinWords, setContentMinWords,
    minQualityScore, setMinQualityScore,
    exactMatch, setExactMatch,
    searchDepth, setSearchDepth,
    aggregation, setAggregation,
    selectedCategories, setSelectedCategories,
    showControlPanel, setShowControlPanel,
    controlPanelLayout, setControlPanelLayout,
    filterHistory, getRecommendations,
    loadPreferences, exportFilters,
    undo, redo, canUndo, canRedo
  } = filters;

  // Core state
  const [mapResults, setMapResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapCollapsed, setMapCollapsed] = useState(false);
  const [showProgrammerMode, setShowProgrammerMode] = useState(false);
  const [hoveredResult, setHoveredResult] = useState(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [containerWidth, setContainerWidth] = useState(800);
  const [showMapStyling, setShowMapStyling] = useState(false);

  // Visualization state
  const [vizModeAdvanced, setVizModeAdvanced] = useState('standard');
  const [dotColorMode, setDotColorMode] = useState('category');
  const [legendFilter, setLegendFilter] = useState(null);
  React.useEffect(() => { setLegendFilter(null); }, [dotColorMode, vizModeAdvanced]);

  const [mapStyle, setMapStyle] = useState(() => {
    const cached = localStorage.getItem('mapStylePreferences');
    return cached ? JSON.parse(cached) : { preset: 'default', tileUrl: null };
  });
  const mapContainerRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  const [vizMode, setVizMode] = useState('category');
  const [activeDatasets, setActiveDatasets] = useState({ category: true, doctype: false, quality: false });
  const activeModes = Object.entries(activeDatasets).filter(([, v]) => v).map(([k]) => k);
  const [dualMapView, setDualMapView] = useState(false);
  const [comparisonVizRight, setComparisonVizRight] = useState('fractal');
  const [mapUrlLimit, setMapUrlLimit] = useState(() => {
    const saved = localStorage.getItem('mapUrlLimit');
    return saved ? parseInt(saved) : 500;
  });
  const [windowOverlay, setWindowOverlay] = useState(null);
  const [plottedWindows, setPlottedWindows] = useState({});

  const toggleDataset = (key) => {
    setActiveDatasets(prev => {
      const next = { ...prev, [key]: !prev[key] };
      if (!Object.values(next).some(v => v)) return prev;
      return next;
    });
  };

  const [dataSource, setDataSource] = useState('worldwide');
  const [showAnalytics, setShowAnalytics] = useState(true);
  const [categories, setCategories] = useState([]);
  const [showCategoryFilter, setShowCategoryFilter] = useState(true);
  const [categoryColorMap, setCategoryColorMap] = useState({});

  const educatedIndividualKeywords = {
    science: ['chemistry', 'physics', 'biology', 'chemical', 'molecule', 'atom', 'reaction', 'experiment', 'laboratory', 'scientific'],
    engineering: ['engineering', 'electrical', 'mechanical', 'civil', 'structural', 'circuit', 'voltage', 'current', 'materials'],
    construction: ['framing', 'drywall', 'plumbing', 'hvac', 'roofing', 'foundation', 'concrete', 'lumber', 'insulation'],
    culinary: ['cooking', 'recipe', 'stock', 'sauce', 'baking', 'roast', 'braise', 'sauté', 'grill', 'smoke'],
    trades: ['welding', 'carpentry', 'masonry', 'plumber', 'electrician', 'hvac tech', 'apprentice', 'journeyman'],
    mathematics: ['calculus', 'algebra', 'geometry', 'trigonometry', 'equation', 'formula', 'theorem'],
    technology: ['programming', 'coding', 'software', 'hardware', 'network', 'database', 'algorithm', 'arduino']
  };

  // Container width tracking
  useEffect(() => {
    const updateWidth = () => {
      if (mapContainerRef.current) setContainerWidth(mapContainerRef.current.clientWidth);
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // Persist URL limit
  useEffect(() => { localStorage.setItem('mapUrlLimit', String(mapUrlLimit)); }, [mapUrlLimit]);

  // Window overlay events
  useEffect(() => {
    const handleShowOnMap = (e) => {
      const { windowId, title, results: windowResults } = e.detail || {};
      if (windowResults && windowResults.length > 0) setWindowOverlay({ windowId, title, results: windowResults });
    };
    const handlePlotWindow = (e) => {
      const { windowId, title, results: windowResults } = e.detail || {};
      if (windowId && windowResults) setPlottedWindows(prev => ({ ...prev, [windowId]: { title, results: windowResults } }));
    };
    const handleRemoveWindow = (e) => {
      const { windowId } = e.detail || {};
      if (windowId) setPlottedWindows(prev => { const next = { ...prev }; delete next[windowId]; return next; });
    };
    window.addEventListener('showWindowOnMap', handleShowOnMap);
    window.addEventListener('plotWindowOnMap', handlePlotWindow);
    window.addEventListener('removeWindowFromMap', handleRemoveWindow);
    return () => {
      window.removeEventListener('showWindowOnMap', handleShowOnMap);
      window.removeEventListener('plotWindowOnMap', handlePlotWindow);
      window.removeEventListener('removeWindowFromMap', handleRemoveWindow);
    };
  }, []);

  // Fetch categories
  const fetchCategories = useCallback(() => {
    return new Promise((resolve) => {
      if (!token) { resolve(); return; }
      get('/categories', {
        onSuccess: (data) => {
          setCategories(data || []);
          const colorMap = {};
          (data || []).forEach((cat, idx) => { colorMap[cat.name] = CATEGORY_COLORS[idx % CATEGORY_COLORS.length]; });
          setCategoryColorMap(colorMap);
        },
        onFinally: () => resolve()
      });
    });
  }, [token, get]);

  // AI Search state
  const [aiSearchQuery, setAiSearchQuery] = useState('');
  const [aiSearchLoading, setAiSearchLoading] = useState(false);
  const [aiSearchMode, setAiSearchMode] = useState('comprehensive');
  const [dbSearchLoading, setDbSearchLoading] = useState(false);
  const [dbSearchMode, setDbSearchMode] = useState('smart');
  const [seaLoading, setSeaLoading] = useState(false);
  const [seaResults, setSeaResults] = useState([]);
  const [searchPreferences, setSearchPreferences] = useState({
    fuzzySearch: false, narrowSearch: false, highQuality: false, favorSchematics: false
  });

  // Toggle category
  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev => prev.includes(categoryId) ? prev.filter(c => c !== categoryId) : [...prev, categoryId]);
  };

  const selectedCategoryNames = React.useMemo(() => {
    return categories.filter(c => selectedCategories.includes(c.id || c._id)).map(c => c.name);
  }, [categories, selectedCategories]);

  const getResultCategoryColor = (result) => {
    if (result.categories && result.categories.length > 0) {
      return categoryColorMap[result.categories[0]] || '#6b7280';
    }
    return getMarkerColor(result.article_type);
  };

  // Filter map results
  const filteredMapResults = React.useMemo(() => {
    let results = mapResults;
    if (selectedCategoryNames.length > 0) {
      if (aggregation === 'and') {
        results = results.filter(r => r.categories && selectedCategoryNames.every(catName => r.categories.includes(catName)));
      } else {
        results = results.filter(r => r.categories && r.categories.some(catName => selectedCategoryNames.includes(catName)));
      }
    }
    if (selectedDocTypes.length > 0 && selectedDocTypes.length < documentTypes.length) {
      results = results.filter(r => selectedDocTypes.includes(r.article_type));
    }
    if (minQualityScore > 0) {
      results = results.filter(r => (r.content_quality_score || 50) >= minQualityScore);
    }
    const activeTopics = Object.entries(educatedIndividualFilters)
      .filter(([k, active]) => active && k !== 'favor_visuals' && k !== 'favor_pictures')
      .map(([topic]) => topic);
    if (activeTopics.length > 0 && activeTopics.length < 7) {
      results = results.filter(r => {
        const text = `${r.title || ''} ${r.snippet || ''} ${r.content || ''}`.toLowerCase();
        return activeTopics.some(topic => (educatedIndividualKeywords[topic] || []).some(kw => text.includes(kw)));
      });
    }
    const hasActiveEITopics = Object.values(educatedIndividualFilters).some(v => v === true);
    if (hasActiveEITopics && minWordCount > 0) {
      results = results.filter(r => {
        const wc = r.word_count || r.content_length || 0;
        return wc === 0 || wc >= minWordCount;
      });
    }
    if (activeTopics.length > 0 && activeTopics.length < 7) {
      results = results.filter(r => {
        const wc = r.word_count || r.content_length || 0;
        if (wc === 0) return true;
        const text = `${r.title || ''} ${r.snippet || ''}`.toLowerCase();
        for (const topic of activeTopics) {
          const kws = educatedIndividualKeywords[topic] || [];
          if (kws.some(kw => text.includes(kw))) {
            if ((eiMinWords[topic] || 0) > 0 && wc < eiMinWords[topic]) return false;
          }
        }
        return true;
      });
    }
    results = results.filter(r => {
      if (!r.article_type) return true;
      const dtMin = docTypeMinWords[r.article_type] || 0;
      if (dtMin <= 0) return true;
      const wc = r.word_count || r.content_length || 0;
      return wc === 0 || wc >= dtMin;
    });
    const activeCertKeys = Object.entries(certificationFilters).filter(([, v]) => v).map(([k]) => k);
    const certKeywordMap = {
      pearsonvue: ['pearsonvue', 'pearson vue', 'certification exam', 'prometric', 'comptia', 'cisco certified'],
      government: ['government', '.gov', 'federal', 'state agency', 'municipal', 'public sector', 'department of'],
      advancedDegree: ['ph.d', 'phd', 'doctorate', 'master of science', 'master of arts', 'dissertation', 'thesis', 'postdoctoral']
    };
    if (activeCertKeys.length > 0 && activeCertKeys.length < 3) {
      results = results.filter(r => {
        const text = `${r.title || ''} ${r.snippet || ''} ${r.url || ''} ${r.article_type || ''}`.toLowerCase();
        return activeCertKeys.some(key => (certKeywordMap[key] || []).some(kw => text.includes(kw)));
      });
      results = results.filter(r => {
        const wc = r.word_count || r.content_length || 0;
        if (wc === 0) return true;
        for (const ck of activeCertKeys) {
          if ((certMinWords[ck] || 0) > 0 && wc < certMinWords[ck]) return false;
        }
        return true;
      });
    }
    if (educatedIndividualFilters.favor_pictures) {
      const pictureKeywords = ['image', 'photo', 'picture', 'gallery', 'illustration', 'figure', 'img', 'photograph', 'infographic'];
      results.sort((a, b) => {
        const aScore = pictureKeywords.filter(kw => `${a.title || ''} ${a.snippet || ''} ${a.url || ''}`.toLowerCase().includes(kw)).length;
        const bScore = pictureKeywords.filter(kw => `${b.title || ''} ${b.snippet || ''} ${b.url || ''}`.toLowerCase().includes(kw)).length;
        return bScore - aScore;
      });
    }
    return results;
  }, [mapResults, selectedCategoryNames, selectedDocTypes, documentTypes.length, educatedIndividualFilters, certificationFilters, aggregation, minQualityScore, minWordCount, eiMinWords, docTypeMinWords, certMinWords]);

  // Effective map results (merge with plotted windows)
  const effectiveMapResults = React.useMemo(() => {
    if (windowOverlay) return windowOverlay.results.filter(r => r.latitude && r.longitude);
    const plottedResults = [];
    Object.values(plottedWindows).forEach(win => {
      (win.results || []).forEach(r => { if (r.latitude && r.longitude) plottedResults.push(r); });
    });
    if (plottedResults.length > 0) {
      const seen = new Set(filteredMapResults.map(r => r.url));
      return [...filteredMapResults, ...plottedResults.filter(r => !seen.has(r.url))];
    }
    return filteredMapResults;
  }, [filteredMapResults, windowOverlay, plottedWindows]);

  // Legend filter
  const legendFilteredResults = React.useMemo(() => {
    if (!legendFilter) return effectiveMapResults;
    return effectiveMapResults.filter(r => {
      if (dotColorMode === 'doctype') return (r.article_type || 'Unknown') === legendFilter;
      return (r.categories?.[0] || 'Uncategorized') === legendFilter;
    });
  }, [effectiveMapResults, legendFilter, dotColorMode]);

  // Location extraction
  const extractLocation = useCallback((text, title) => {
    if (!text && !title) return [];
    const combined = `${title || ''} ${text || ''}`.toLowerCase();
    const locations = [];
    const seen = new Set();
    for (const [place, coords] of Object.entries(KNOWN_LOCATIONS)) {
      if (combined.includes(place) && !seen.has(`${coords.lat},${coords.lng}`)) {
        seen.add(`${coords.lat},${coords.lng}`);
        locations.push({ lat: coords.lat + (Math.random() - 0.5) * 1.5, lng: coords.lng + (Math.random() - 0.5) * 1.5, place });
      }
    }
    const zipRegex = /\b(\d{5})(?:-\d{4})?\b/g;
    const zipMatches = combined.match(zipRegex);
    if (zipMatches && locations.length < 8) {
      zipMatches.forEach(zip => {
        const prefix = zip[0];
        const region = ZIP_REGIONS[prefix];
        if (region && !seen.has(`zip-${prefix}`)) {
          seen.add(`zip-${prefix}`);
          locations.push({ lat: region.lat + (Math.random() - 0.5) * 2, lng: region.lng + (Math.random() - 0.5) * 2, place: `ZIP ${zip} (${region.place})` });
        }
      });
    }
    return locations;
  }, []);

  // Initial data load
  useEffect(() => {
    if (token) {
      fetchCategories();
      const timer = setTimeout(() => { fetchMapResults(); }, 500);
      return () => clearTimeout(timer);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch map results
  const fetchMapResults = useCallback((showLoadingState = true) => {
    if (!token) return;
    if (showLoadingState) setLoading(true);
    const queryParams = new URLSearchParams();
    queryParams.append('limit', '500');
    if (searchPreferences.fuzzySearch) queryParams.append('fuzzy', 'true');
    if (searchPreferences.narrowSearch) queryParams.append('narrow', 'true');
    if (searchPreferences.highQuality) queryParams.append('high_quality', 'true');
    if (searchPreferences.favorSchematics) queryParams.append('favor_schematics', 'true');
    if (dataSource !== 'worldwide') queryParams.append('personal', 'true');
    const endpoint = `${API}/map/worldwide?${queryParams.toString()}`;
    const xhr = new XMLHttpRequest();
    xhr.open('GET', endpoint, true);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.onload = function() {
      if (xhr.status === 200) {
        try {
          const data = JSON.parse(xhr.responseText);
          const results = data.results || data;
          const resultsWithLocation = [];
          const arr = Array.isArray(results) ? results : [];
          for (let i = 0; i < arr.length; i++) {
            try {
              const r = arr[i];
              if (r.latitude != null && r.longitude != null &&
                  Number(r.latitude) >= -90 && Number(r.latitude) <= 90 &&
                  Number(r.longitude) >= -180 && Number(r.longitude) <= 180) {
                resultsWithLocation.push({ ...r, latitude: Number(r.latitude), longitude: Number(r.longitude), hasRealLocation: true, hashtags: extractHashtags(r.title, r.snippet, r.article_type) });
              }
              const combinedText = [r.content, r.snippet, r.url].filter(Boolean).join(' ');
              const extractedLocations = extractLocation(combinedText, r.title);
              for (const loc of extractedLocations) {
                const isDuplicate = resultsWithLocation.some(m => m.id === r.id && Math.abs((m.latitude || 0) - loc.lat) < 0.5 && Math.abs((m.longitude || 0) - loc.lng) < 0.5);
                if (!isDuplicate) {
                  resultsWithLocation.push({ ...r, latitude: loc.lat, longitude: loc.lng, extractedPlace: loc.place, hashtags: extractHashtags(r.title, r.snippet, r.article_type) });
                }
              }
            } catch (itemErr) { /* skip */ }
          }
          setMapResults(resultsWithLocation);
          setLastUpdate(new Date());
        } catch (e) { /* skip */ }
      }
      if (showLoadingState) setLoading(false);
    };
    xhr.onerror = function() { if (showLoadingState) setLoading(false); };
    xhr.send();
  }, [token, extractLocation, dataSource, searchPreferences]);

  // Export map data
  const exportMapData = (format) => {
    if (mapResults.length === 0) { showToast('No data to export', 'error'); return; }
    const exportData = mapResults.map(r => ({
      title: r.title || 'Untitled', url: r.url || '', latitude: r.latitude, longitude: r.longitude,
      article_type: r.article_type || 'Unknown', location: r.extractedPlace || 'Unknown',
      snippet: (r.snippet || '').substring(0, 200), hashtags: (r.hashtags || []).join(', ')
    }));
    let content, filename, mimeType;
    if (format === 'csv') {
      const headers = ['Title', 'URL', 'Latitude', 'Longitude', 'Article Type', 'Location', 'Snippet', 'Hashtags'];
      const csvRows = [headers.join(','), ...exportData.map(row => [
        `"${(row.title || '').replace(/"/g, '""')}"`, `"${row.url}"`, row.latitude, row.longitude,
        `"${row.article_type}"`, `"${row.location}"`, `"${(row.snippet || '').replace(/"/g, '""')}"`, `"${row.hashtags}"`
      ].join(','))];
      content = csvRows.join('\n');
      filename = `infopilot_map_export_${new Date().toISOString().slice(0, 10)}.csv`;
      mimeType = 'text/csv';
    } else {
      content = JSON.stringify(exportData, null, 2);
      filename = `infopilot_map_export_${new Date().toISOString().slice(0, 10)}.json`;
      mimeType = 'application/json';
    }
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = filename;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    showToast(`Exported ${exportData.length} results as ${format.toUpperCase()}!`, 'success');
  };

  // Search functions
  const searchEngineAccessForMap = () => {
    if (!aiSearchQuery.trim()) { showToast && showToast('Please enter a search query', 'error'); return; }
    setSeaLoading(true);
    post('/search-engine-access', {
      body: { query: aiSearchQuery, engines: ['google', 'bing', 'duckduckgo', 'brave'], limit: 100 },
      onSuccess: (data) => { setSeaResults(data.results || []); showToast && showToast(`${data.message}`, 'success'); fetchMapResults(true); },
      onError: (errMsg) => { showToast && showToast(errMsg || 'Search Engine Access failed', 'error'); },
      onFinally: () => { setSeaLoading(false); }
    });
  };

  const toggleSearchPreference = (key) => {
    setSearchPreferences(prev => ({ ...prev, [key]: !prev[key] }));
    fetchMapResults();
  };

  const aiSearchForMap = () => {
    if (!aiSearchQuery.trim()) { showToast('Please enter a search query', 'error'); return; }
    setAiSearchLoading(true);
    post('/ai-search', {
      body: { query: aiSearchQuery, mode: aiSearchMode, auto_categorize: true },
      onSuccess: (data) => { showToast(`${data.message}`, 'success'); fetchMapResults(true); },
      onError: (errMsg) => { showToast(errMsg || 'AI Search failed', 'error'); },
      onFinally: () => { setAiSearchLoading(false); }
    });
  };

  const autoCategorizeSearc = () => {
    if (!aiSearchQuery.trim()) { showToast('Please enter a search query', 'error'); return; }
    setAiSearchLoading(true);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/auto-categorize`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.onload = function() {
      if (xhr.status === 200) {
        const data = JSON.parse(xhr.responseText);
        if (data.task_id) {
          showToast('Auto-categorize started in background...', 'info');
          const startTime = Date.now();
          const MAX_POLL_TIME = 300000;
          const pollInterval = setInterval(() => {
            if (Date.now() - startTime > MAX_POLL_TIME) {
              clearInterval(pollInterval); showToast('Auto-categorize is taking too long.', 'warning'); setAiSearchLoading(false); return;
            }
            const pollXhr = new XMLHttpRequest();
            pollXhr.open('GET', `${API}/task-status/${data.task_id}`, true);
            pollXhr.setRequestHeader('Authorization', `Bearer ${token}`);
            pollXhr.onload = function() {
              if (pollXhr.status === 200) {
                const task = JSON.parse(pollXhr.responseText);
                if (task.status === 'completed') { clearInterval(pollInterval); showToast(`${task.result?.message || 'Auto-categorize complete!'}`, 'success'); fetchMapResults(true); setAiSearchLoading(false); }
                else if (task.status === 'failed') { clearInterval(pollInterval); showToast(`Auto-categorize failed: ${task.error || 'Unknown error'}`, 'error'); setAiSearchLoading(false); }
              }
            };
            pollXhr.onerror = function() { clearInterval(pollInterval); setAiSearchLoading(false); };
            pollXhr.send();
          }, 3000);
        } else {
          showToast(`${data.message}`, 'success'); fetchMapResults(true); setAiSearchLoading(false);
        }
      } else {
        try { showToast(JSON.parse(xhr.responseText).detail || 'Auto-categorization failed', 'error'); } catch(e) { showToast('Auto-categorization failed', 'error'); }
        setAiSearchLoading(false);
      }
    };
    xhr.onerror = function() { setAiSearchLoading(false); showToast('Auto-categorization failed', 'error'); };
    xhr.send(JSON.stringify({ query: aiSearchQuery }));
  };

  const databaseSearchForMap = () => {
    if (!aiSearchQuery.trim()) { showToast('Please enter a search query', 'error'); return; }
    setDbSearchLoading(true);
    post('/database-search', {
      body: { query: aiSearchQuery, mode: dbSearchMode, limit: 100 },
      onSuccess: (data) => { showToast(`${data.message}`, 'success'); fetchMapResults(true); },
      onError: (errMsg) => { showToast(errMsg || 'Database search failed', 'error'); },
      onFinally: () => { setDbSearchLoading(false); }
    }).catch(() => { setDbSearchLoading(false); });
  };

  // Re-fetch on data source / preference change
  useEffect(() => {
    if (token && mapResults.length > 0) fetchMapResults();
  }, [dataSource, searchPreferences, mapUrlLimit]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh / Clean / Clear dots
  const [refreshingDots, setRefreshingDots] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState('');
  const [totalMapped, setTotalMapped] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [cleaningDots, setCleaningDots] = useState(false);
  const [clearingDots, setClearingDots] = useState(false);
  const [cleanProgress, setCleanProgress] = useState('');

  const cleanMapDots = useCallback(async () => {
    setCleaningDots(true); setCleanProgress('Re-verifying all URLs against protocols...');
    try {
      const res = await new Promise((resolve, reject) => {
        const x = new XMLHttpRequest();
        x.open('POST', `${API}/map/clean-dots`, true);
        x.setRequestHeader('Content-Type', 'application/json');
        x.setRequestHeader('Authorization', `Bearer ${token}`);
        x.onload = () => { try { resolve(JSON.parse(x.responseText)); } catch { reject(); } };
        x.onerror = reject; x.send('{}');
      });
      setCleanProgress(res.message || 'Done!');
      showToast(`Cleaned: ${res.category_assignments_removed} mismatched removed, ${res.category_assignments_kept} kept`, 'success');
      fetchMapResults(true);
    } catch { setCleanProgress('Error during cleaning.'); showToast('Failed to clean map dots', 'error'); }
    setCleaningDots(false);
  }, [token, fetchMapResults, showToast]);

  const clearAllDots = useCallback(async () => {
    if (!window.confirm('Are you sure you want to delete ALL search results and start with a completely fresh map? This cannot be undone.')) return;
    setClearingDots(true); setCleanProgress('Clearing all dots...');
    try {
      const res = await new Promise((resolve, reject) => {
        const x = new XMLHttpRequest();
        x.open('POST', `${API}/map/clear-all-dots`, true);
        x.setRequestHeader('Content-Type', 'application/json');
        x.setRequestHeader('Authorization', `Bearer ${token}`);
        x.onload = () => { try { resolve(JSON.parse(x.responseText)); } catch { reject(); } };
        x.onerror = reject; x.send('{}');
      });
      setCleanProgress(res.message || 'Done!');
      showToast(`Cleared ${res.deleted} results. Fresh map ready!`, 'success');
      fetchMapResults(true);
    } catch { setCleanProgress('Error during clearing.'); showToast('Failed to clear dots', 'error'); }
    setClearingDots(false);
  }, [token, fetchMapResults, showToast]);

  const refreshMapDots = useCallback(async () => {
    setRefreshingDots(true); setRefreshProgress('Starting batch geocoding...');
    let totalGeo = 0, calls = 0;
    while (calls < 50) {
      calls++;
      try {
        const res = await new Promise((resolve, reject) => {
          const x = new XMLHttpRequest();
          x.open('POST', `${API}/map/refresh-dots`, true);
          x.setRequestHeader('Content-Type', 'application/json');
          x.setRequestHeader('Authorization', `Bearer ${token}`);
          x.onload = () => { try { resolve(JSON.parse(x.responseText)); } catch { reject(); } };
          x.onerror = reject; x.send('{}');
        });
        totalGeo += (res.text_geocoded || 0) + (res.ip_geocoded || 0);
        setRefreshProgress(`Batch ${calls}: ${res.text_geocoded} text + ${res.ip_geocoded} IP geocoded. ${res.remaining} remaining...`);
        if (res.remaining === 0 || res.processed === 0) break;
      } catch { break; }
    }
    setRefreshProgress(`Done! Geocoded ${totalGeo} URLs total.`);
    showToast(`Refresh complete: ${totalGeo} URLs geocoded`, 'success');
    fetchMapResults(true);
    setRefreshingDots(false);
  }, [token, fetchMapResults, showToast]);

  useEffect(() => { if (!token) return;
    const x = new XMLHttpRequest();
    x.open('GET', `${API}/search/count`, true);
    x.setRequestHeader('Authorization', `Bearer ${token}`);
    x.onload = () => { try { const d = JSON.parse(x.responseText); setTotalResults(d.total || d.count || 0); } catch {} };
    x.send();
  }, [token]);

  useEffect(() => { setTotalMapped(effectiveMapResults.length); }, [effectiveMapResults]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh && token) {
      refreshIntervalRef.current = setInterval(() => { fetchMapResults(false); }, MAP_REFRESH_INTERVAL);
    }
    return () => { if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current); };
  }, [autoRefresh, token, fetchMapResults]);

  useEffect(() => {
    const handleMapRefresh = () => { fetchMapResults(false); };
    window.addEventListener('infopilot-data-changed', handleMapRefresh);
    return () => { window.removeEventListener('infopilot-data-changed', handleMapRefresh); };
  }, [fetchMapResults]);

  // Marker helpers
  const getMarkerColor = (articleType) => {
    const colors = { 'News Article': '#ef4444', 'Blog Post': '#f97316', 'Academic Paper': '#3b82f6', 'Wiki': '#10b981', 'Forum': '#FFD54F', 'Government': '#06b6d4', 'Video': '#FFD700', 'Unknown': '#6b7280' };
    return colors[articleType] || colors['Unknown'];
  };

  // Popup state
  const [isPopupMaximized, setIsPopupMaximized] = useState(false);
  const popupTimeoutRef = useRef(null);

  const handleMarkerClick = (result) => { if (result.url) window.open(result.url, '_blank', 'noopener,noreferrer'); };

  const handleMarkerHover = (result, event) => {
    if (popupTimeoutRef.current) { clearTimeout(popupTimeoutRef.current); popupTimeoutRef.current = null; }
    if (mapContainerRef.current) {
      const rect = mapContainerRef.current.getBoundingClientRect();
      setHoverPosition({ x: event.originalEvent.clientX - rect.left, y: event.originalEvent.clientY - rect.top });
    }
    setHoveredResult(result); setIsPopupMaximized(false);
  };

  const handleMarkerLeave = () => {
    popupTimeoutRef.current = setTimeout(() => { setHoveredResult(null); setIsPopupMaximized(false); }, 200);
  };

  const handlePopupMouseEnter = () => { if (popupTimeoutRef.current) { clearTimeout(popupTimeoutRef.current); popupTimeoutRef.current = null; } };
  const handlePopupMouseLeave = () => { popupTimeoutRef.current = setTimeout(() => { setHoveredResult(null); setIsPopupMaximized(false); }, 150); };
  const closePopup = () => { if (popupTimeoutRef.current) { clearTimeout(popupTimeoutRef.current); popupTimeoutRef.current = null; } setHoveredResult(null); setIsPopupMaximized(false); };
  const togglePopupMaximize = () => { setIsPopupMaximized(prev => !prev); };

  useEffect(() => { return () => { if (popupTimeoutRef.current) clearTimeout(popupTimeoutRef.current); }; }, []);

  return {
    // Auth
    token, user,
    // Filters (pass-through)
    filters,
    educatedIndividualFilters, setEducatedIndividualFilters,
    eiMinWords, setEiMinWords,
    minWordCount, setMinWordCount,
    documentTypes, selectedDocTypes, setSelectedDocTypes,
    docTypeMinWords, setDocTypeMinWords,
    certificationFilters, setCertificationFilters,
    certMinWords, setCertMinWords,
    contentMinWords, setContentMinWords,
    minQualityScore, setMinQualityScore,
    exactMatch, setExactMatch,
    searchDepth, setSearchDepth,
    aggregation, setAggregation,
    selectedCategories, setSelectedCategories,
    showControlPanel, setShowControlPanel,
    controlPanelLayout, setControlPanelLayout,
    filterHistory, getRecommendations,
    loadPreferences, exportFilters,
    undo, redo, canUndo, canRedo,
    // Core state
    mapResults, loading, mapCollapsed, setMapCollapsed,
    showProgrammerMode, setShowProgrammerMode,
    hoveredResult, hoverPosition, autoRefresh, setAutoRefresh,
    lastUpdate, containerWidth, showMapStyling, setShowMapStyling,
    // Viz
    vizModeAdvanced, setVizModeAdvanced, dotColorMode, setDotColorMode,
    legendFilter, setLegendFilter, mapStyle, setMapStyle,
    mapContainerRef, vizMode, setVizMode,
    activeDatasets, activeModes, toggleDataset,
    dualMapView, setDualMapView, comparisonVizRight, setComparisonVizRight,
    mapUrlLimit, setMapUrlLimit, windowOverlay, setWindowOverlay,
    plottedWindows,
    dataSource, setDataSource, showAnalytics, setShowAnalytics,
    // Categories
    categories, showCategoryFilter, setShowCategoryFilter, categoryColorMap,
    toggleCategory, selectedCategoryNames,
    // Computed
    filteredMapResults, effectiveMapResults, legendFilteredResults,
    getResultCategoryColor, getMarkerColor,
    // Search
    aiSearchQuery, setAiSearchQuery, aiSearchLoading, aiSearchMode, setAiSearchMode,
    dbSearchLoading, dbSearchMode, setDbSearchMode,
    seaLoading, seaResults, searchPreferences, toggleSearchPreference,
    // Actions
    fetchCategories, fetchMapResults, exportMapData,
    aiSearchForMap, autoCategorizeSearc, databaseSearchForMap, searchEngineAccessForMap,
    // Dots
    refreshingDots, refreshProgress, totalMapped, totalResults,
    cleaningDots, clearingDots, cleanProgress,
    cleanMapDots, clearAllDots, refreshMapDots,
    // Marker interactions
    isPopupMaximized, handleMarkerClick, handleMarkerHover, handleMarkerLeave,
    handlePopupMouseEnter, handlePopupMouseLeave, closePopup, togglePopupMaximize,
  };
};

export { CATEGORY_COLORS };
export default useMapPageData;
